/**
 * Google Analytics 4 (gtag.js) 連携。
 *
 * 測定 ID は環境変数 REACT_APP_GA_MEASUREMENT_ID から注入する。
 * CI は `cp .env.<env>-backend .env` してからビルドするため、
 * 本番 (.env.prd-backend) にのみ ID を置いておけば dev / local では自動的に無効になる。
 *
 * SPA なので gtag の自動 page_view は使わず (send_page_view: false)、
 * ルート遷移ごとに trackPageView を呼ぶ (hooks/use-analytics-page-view)。
 *
 * 送信するパスは normalizePath で ID を伏せ、クエリ文字列は一切送らない。
 * URL には案件 ID・人材 ID・検索キーワードなど顧客データが載るため、
 * それらを GA へ流出させないことと、ページ一覧が ID で膨張するのを防ぐのが目的。
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID ?? "";

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

const GTAG_SCRIPT_ID = "ga4-gtag-js";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_PATTERN = /^\d+$/;
/** 静的なルートセグメントの形（小文字の語をハイフンで連結）。例: notification-settings */
const STATIC_SEGMENT_PATTERN = /^[a-z]+(?:-[a-z]+)*$/;

/**
 * パスセグメントが ID / トークンの類かを判定する。
 *
 * 静的セグメントは小文字の語をハイフンで繋いだ短い形に限られるため、
 * それ以外（UUID・連番・大文字や数字を含む長い文字列・極端に長い文字列）は
 * ID とみなして伏せる。判定に迷う場合は「伏せる」側に倒す。
 * @param {string} segment パスセグメント
 * @return {boolean} ID とみなす場合 true
 */
function isIdSegment(segment: string): boolean {
  if (UUID_PATTERN.test(segment)) {
    return true;
  }
  if (NUMERIC_PATTERN.test(segment)) {
    return true;
  }
  if (segment.length >= 24) {
    return true;
  }
  return segment.length >= 12 && !STATIC_SEGMENT_PATTERN.test(segment);
}

/**
 * 計測用にパスを正規化する（ID セグメントを :id に置換、クエリ・ハッシュは除去）。
 * @param {string} pathname 元のパス（クエリ・ハッシュを含んでいてもよい）
 * @return {string} 正規化済みパス。例: /crm/talents/:id/edit
 */
export function normalizePath(pathname: string): string {
  const pathOnly = pathname.split(/[?#]/)[0];
  const normalized = pathOnly
    .split("/")
    .map(segment => (isIdSegment(segment) ? ":id" : segment))
    .join("/");
  return normalized === "" ? "/" : normalized;
}

/**
 * GA4 が有効か（測定 ID が注入されているか）を返す。
 * @return {boolean} 有効な場合 true
 */
export function isAnalyticsEnabled(): boolean {
  return MEASUREMENT_ID !== "";
}

/**
 * gtag.js を読み込み、GA4 を初期化する。アプリ起動時に一度だけ呼ぶ。
 *
 * 測定 ID が無い環境（dev / local / テスト）では何もしない。
 * @return {void}
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled() || typeof window === "undefined") {
    return;
  }
  const w = window as GtagWindow;
  if (document.getElementById(GTAG_SCRIPT_ID)) {
    return;
  }

  w.dataLayer = w.dataLayer || [];
  // gtag.js は dataLayer に積まれた Arguments オブジェクトを読む仕様のため、
  // 公式スニペットどおり arguments をそのまま push する（rest 引数の配列では解釈されない）。
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer?.push(arguments);
  };

  const script = document.createElement("script");
  script.id = GTAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    MEASUREMENT_ID,
  )}`;
  document.head.appendChild(script);

  w.gtag("js", new Date());
  // 自動 page_view は無効化し、ルート遷移フックから明示的に送る
  // （以下のパラメータ名は gtag.js の仕様で snake_case 固定）
  w.gtag("config", MEASUREMENT_ID, { send_page_view: false });
}

/**
 * page_view を送信する。
 * @param {string} pathname 遷移先のパス（正規化前でよい）
 * @return {void}
 */
export function trackPageView(pathname: string): void {
  if (!isAnalyticsEnabled() || typeof window === "undefined") {
    return;
  }
  const w = window as GtagWindow;
  if (!w.gtag) {
    return;
  }

  const path = normalizePath(pathname);
  // パラメータ名は gtag.js の仕様で snake_case 固定
  w.gtag("event", "page_view", {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    // document.title には人材名など個人情報が載りうるため使わない
    page_title: path,
  });
}
